import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  LogOut,
  Menu,
  Moon,
  Search,
  Sun
} from 'lucide-react'
import { useAuth } from '@contexts/AuthContext'
import { useTheme } from '@contexts/ThemeContext'
import { useAdminSession } from '@hooks/useAdminSession'
import AdminAttentionBadge from './AdminAttentionBadge'
import AdminEnvironmentBadge from './AdminEnvironmentBadge'
import AdminModeIndicator from './AdminModeIndicator'

function formatRole(role) {
  return String(role || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, character => character.toUpperCase())
}

export default function AdminTopbar({ onOpenNavigation, onOpenSearch }) {
  const { user, logout } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const { session } = useAdminSession()
  const role = session?.principal?.role || user?.role

  return (
    <header className="sticky top-0 z-30 flex h-16 flex-none items-center border-b border-gray-200 bg-white px-3 dark:border-gray-800 dark:bg-gray-900 sm:px-4 lg:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <button
          type="button"
          onClick={onOpenNavigation}
          className="inline-flex h-9 w-9 flex-none items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-950 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-white md:hidden"
          aria-label="Open Platform Console navigation"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>

        <span className="truncate text-sm font-semibold text-gray-950 dark:text-white md:hidden">
          Platform Console
        </span>

        <button
          type="button"
          onClick={onOpenSearch}
          className="ml-1 hidden h-9 min-w-0 max-w-sm flex-1 items-center gap-2 border border-gray-300 bg-gray-50 px-3 text-left text-sm text-gray-500 hover:border-gray-400 hover:bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:bg-gray-850 sm:flex"
        >
          <Search className="h-4 w-4 flex-none" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">Search console</span>
          <kbd className="hidden border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400 lg:inline">
            Ctrl K
          </kbd>
        </button>
      </div>

      <div className="ml-3 flex flex-none items-center gap-1.5">
        <span className="hidden sm:inline-flex">
          <AdminEnvironmentBadge environment={session?.environment?.name} />
        </span>
        <AdminModeIndicator />
        <AdminAttentionBadge />
        <button
          type="button"
          onClick={onOpenSearch}
          className="inline-flex h-9 w-9 items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-950 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white sm:hidden"
          aria-label="Search Platform Console"
          title="Search Platform Console"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={toggleTheme}
          className="hidden h-9 w-9 items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-950 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white sm:inline-flex"
          aria-label={isDark ? 'Use light theme' : 'Use dark theme'}
          title={isDark ? 'Use light theme' : 'Use dark theme'}
        >
          {isDark ? (
            <Sun className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Moon className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        <Link
          to="/"
          className="hidden min-h-9 items-center gap-2 border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-950 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-white lg:inline-flex"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Dashboard
        </Link>
        <span
          className="hidden max-w-28 truncate px-2 text-xs font-medium text-gray-600 dark:text-gray-300 xl:inline"
          title={formatRole(role)}
        >
          {formatRole(role)}
        </span>
        <button
          type="button"
          onClick={logout}
          className="inline-flex h-9 w-9 items-center justify-center text-gray-500 hover:bg-red-50 hover:text-red-700 dark:text-gray-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
          aria-label="Log out"
          title="Log out"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </header>
  )
}
